const DAY = 86400000;
const VIEWS = ["Tag", "Woche", "Monat", "Jahr"];

const iso = date => date.toISOString().slice(0, 10);
const utcDate = value => new Date(`${value}T00:00:00Z`);
const addDays = (value, days) => iso(new Date(utcDate(value).valueOf() + days * DAY));

export function mondayOf(value) {
  const date = utcDate(value);
  const offset = (date.getUTCDay() + 6) % 7;
  return addDays(value, -offset);
}

export function fiscalYearFor(value) {
  const date = utcDate(value);
  const startYear = date.getUTCMonth() >= 9 ? date.getUTCFullYear() : date.getUTCFullYear() - 1;
  return { from: `${startYear}-10-01`, to: `${startYear + 1}-09-30` };
}

/** The selectable planning year is identified by the year in which it starts. */
export const planningYearFor = value => Number(fiscalYearFor(value).from.slice(0, 4));

export function planningYearPeriod(year) {
  const startYear = Number(year);
  if (!Number.isInteger(startYear)) return fiscalYearFor(iso(new Date()));
  return { from: `${startYear}-10-01`, to: `${startYear + 1}-09-30` };
}

export const planningYearLabel = year => {
  const period = planningYearPeriod(year);
  return `Planjahr ${period.from.slice(0, 4)}/${period.to.slice(0, 4)}`;
};

/** Complete, shared label for an October-based fiscal year. */
export const fiscalYearLabel = year => {
  const period = planningYearPeriod(year);
  return `Geschäftsjahr ${period.from.slice(0, 4)}/${period.to.slice(0, 4)}`;
};

/** Converts an October-based fiscal week to its Monday. */
export function fiscalWeekDate(fiscalYear, week, { clamp = true } = {}) {
  const startYear = Number(fiscalYear), parsedWeek = Number(week);
  if (!Number.isInteger(startYear) || !Number.isInteger(parsedWeek)) return null;
  if (!clamp && (parsedWeek < 1 || parsedWeek > 52)) return null;
  const safeWeek = Math.max(1, Math.min(52, parsedWeek));
  return addDays(mondayOf(`${startYear}-10-01`), (safeWeek - 1) * 7);
}

export const fiscalYearStartFor = value => Number(fiscalYearFor(value).from.slice(0, 4));

/** Returns the ISO-8601 week and week-year containing the supplied date. */
export function isoWeekFor(value) {
  const date = utcDate(value);
  const thursday = new Date(date.valueOf());
  thursday.setUTCDate(date.getUTCDate() + 3 - ((date.getUTCDay() + 6) % 7));
  const year = thursday.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(year, 0, 4));
  firstThursday.setUTCDate(
    firstThursday.getUTCDate() + 3 - ((firstThursday.getUTCDay() + 6) % 7),
  );
  return { week: 1 + Math.round((thursday - firstThursday) / (7 * DAY)), year };
}

/** December 28 always falls in the final ISO week of its ISO year. */
export function isoWeeksInYear(year) {
  const parsedYear = Number(year);
  if (!Number.isInteger(parsedYear) || !/^\d{4}$/.test(String(parsedYear))) return 0;
  return isoWeekFor(`${parsedYear}-12-28`).week;
}

/** ISO week 1 contains January 4; its Monday can be in December. */
export function isoWeekDate(year, week) {
  const parsedYear = Number(year), parsedWeek = Number(week);
  if (!Number.isInteger(parsedWeek) || parsedWeek < 1 || parsedWeek > isoWeeksInYear(parsedYear)) return null;
  return addDays(mondayOf(`${parsedYear}-01-04`), (parsedWeek - 1) * 7);
}

/** Creates a stable planning anchor from a numeric year and one-based month. */
export function monthDate(year, month) {
  const parsedYear = Number(year), parsedMonth = Number(month);
  if (!Number.isInteger(parsedYear) || !Number.isInteger(parsedMonth) || parsedMonth < 1 || parsedMonth > 12) return null;
  return `${parsedYear}-${String(parsedMonth).padStart(2, "0")}-01`;
}

export function planningPeriod(view, anchor) {
  const safeView = VIEWS.includes(view) ? view : "Woche";
  if (safeView === "Tag") return { view: safeView, anchor, from: anchor, to: anchor };
  if (safeView === "Woche") {
    const from = mondayOf(anchor);
    return { view: safeView, anchor, from, to: addDays(from, 6) };
  }
  if (safeView === "Monat") {
    const date = utcDate(anchor);
    const from = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-01`;
    const to = iso(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)));
    return { view: safeView, anchor, from, to };
  }
  return { view: safeView, anchor, ...fiscalYearFor(anchor) };
}

export function movePlanningPeriod(period, direction) {
  if (period.view === "Tag") return addDays(period.anchor, direction);
  if (period.view === "Woche") return addDays(period.anchor, direction * 7);
  const date = utcDate(period.anchor);
  if (period.view === "Monat") return iso(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + direction, 1)));
  return iso(new Date(Date.UTC(date.getUTCFullYear() + direction, date.getUTCMonth(), date.getUTCDate())));
}

export const inPlanningPeriod = (entry, period) => entry.datum >= period.from && entry.datum <= period.to;

export function planningPeriodLabel(period) {
  const format = value => new Intl.DateTimeFormat("de-CH", { timeZone: "UTC" }).format(utcDate(value));
  if (period.view === "Tag") return format(period.from);
  if (period.view === "Monat") return new Intl.DateTimeFormat("de-CH", { month: "long", year: "numeric", timeZone: "UTC" }).format(utcDate(period.from));
  if (period.view === "Jahr") return fiscalYearLabel(period.from.slice(0, 4));
  const short = value => new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit", timeZone: "UTC" }).format(utcDate(value));
  const end = new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }).format(utcDate(period.to));
  return `KW ${isoWeekFor(period.from).week} · ${short(period.from)}–${end}`;
}

/** Compact ISO-8601 week label used by the Dashboard (not planning views). */
export function dashboardWeekLabel(period) {
  const { week, year } = isoWeekFor(period.from || period.anchor);
  return `Woche KW${week}/${String(year).slice(-2)}`;
}
